// Typed facade over d3-dag for the DAG layout pipeline.
//
// d3-dag's public types are sound but spread across a generic Ops/Graph
// hierarchy that's awkward to thread by hand at call sites. This module
// pins those generics to the single datum shape `dag-layout` actually
// uses ({ id, parentIds }) and re-exports the operators with concrete
// types so consumers don't reach for `any` to bridge the gap.
//
// The runtime is pure d3-dag — no monkey-patching, no behaviour change.

import {
  graphStratify,
  sugiyama,
  decrossTwoLayer,
  coordCenter,
  zherebko,
  type Graph,
  type GraphLink,
  type GraphNode,
  type MutGraph,
} from "d3-dag";

/** The single stratify-input shape we feed d3-dag. */
export interface DagDatum {
  readonly id: string;
  readonly parentIds: ReadonlyArray<string>;
}

/** A laid-out graph: nodes carry mutable x/y after a layout pass. */
export type DagGraph = MutGraph<DagDatum, undefined>;
export type DagNode = GraphNode<DagDatum, undefined>;
export type DagLink = GraphLink<DagDatum, undefined>;

/** Build a d3-dag graph from a stratify-shaped datum list. */
export const buildDagGraph = (data: ReadonlyArray<DagDatum>): DagGraph => {
  const strat = graphStratify();
  // graphStratify's default operator preserves the input element type, so the
  // returned MutGraph carries our DagDatum without a cast.
  return strat(data);
};

/**
 * Run the row-wrapped Sugiyama layout. Returns the same graph with node
 * x/y populated. Mirrors d3-dag's "mutate in place, hand back a handle"
 * convention so callers can keep iterating the same value.
 */
export const layoutSugiyama = (
  dag: DagGraph,
  nodeWidthGap: number,
  nodeHeightGap: number,
): DagGraph => {
  const layout = sugiyama()
    .decross(decrossTwoLayer())
    .coord(coordCenter())
    .nodeSize([nodeWidthGap, nodeHeightGap]);
  layout(dag as Graph<DagDatum, undefined>);
  return dag;
};

/** Run the Zherebko (single-column topological) layout. */
export const layoutZherebko = (
  dag: DagGraph,
  nodeWidthGap: number,
  nodeHeightGap: number,
): DagGraph => {
  const layout = zherebko().nodeSize([nodeWidthGap, nodeHeightGap]);
  layout(dag as Graph<DagDatum, undefined>);
  return dag;
};

/** Materialise nodes once so callers can iterate twice without re-walking. */
export const dagNodes = (dag: DagGraph): ReadonlyArray<DagNode> => [
  ...dag.nodes(),
];

/** Materialise links once. */
export const dagLinks = (dag: DagGraph): ReadonlyArray<DagLink> => [
  ...dag.links(),
];
